import { useState } from "react";
import { Siren, Phone, Plane, Hotel, ShieldCheck, MapPin, X } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

/* One-tap Travel Emergency Card — big, high-contrast, tel: links. No sensitive documents. */
export default function EmergencyCard({ tripId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const show = async () => {
    setOpen(true);
    if (!data) {
      try { setData((await api.get(`/trips/${tripId}/emergency`)).data); }
      catch { toast.error("Could not load emergency info"); setOpen(false); }
    }
  };

  return (
    <>
      <button onClick={show} data-testid="emergency-card-btn"
        className="inline-flex items-center gap-1.5 border border-rose-600 bg-rose-600 text-white px-3 py-2 text-sm font-700 hover:bg-rose-700 transition-colors">
        <Siren className="w-4 h-4" /> Emergency card
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-zinc-950/95 overflow-y-auto" data-testid="emergency-card">
          <div className="max-w-lg mx-auto px-5 py-8 text-white">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-900 text-2xl flex items-center gap-2"><Siren className="w-6 h-6 text-rose-400" /> Travel Emergency Card</h2>
              <button onClick={() => setOpen(false)} data-testid="emergency-close" className="p-2 text-zinc-400 hover:text-white"><X className="w-6 h-6" /></button>
            </div>
            {!data ? <p className="animate-pulse">Loading…</p> : (
              <div className="space-y-5">
                <div className="border border-zinc-700 p-4">
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-400">Trip</div>
                  <div className="text-xl font-900">{data.trip.title}</div>
                  <div className="text-sm text-zinc-300">{[data.trip.start_date, data.trip.end_date].filter(Boolean).join(" – ")}</div>
                  {data.trip.stops?.length > 0 && <div className="text-sm text-zinc-300 flex items-center gap-1 mt-0.5"><MapPin className="w-3.5 h-3.5" />{data.trip.stops.join(" → ")}</div>}
                </div>
                {data.next_flight && (
                  <div className="border border-sky-800 bg-sky-950/50 p-4" data-testid="emergency-flight">
                    <div className="text-xs font-mono uppercase tracking-widest text-sky-400 flex items-center gap-1"><Plane className="w-3.5 h-3.5" /> Next flight</div>
                    <div className="text-lg font-900">{data.next_flight.ref} {data.next_flight.carrier && `· ${data.next_flight.carrier}`}</div>
                    <div className="text-sm">{data.next_flight.from_place} → {data.next_flight.to_place}</div>
                    <div className="text-sm text-zinc-300">{data.next_flight.date} {data.next_flight.time?.slice(0, 5)}
                      {data.next_flight.seat && ` · Seat ${data.next_flight.seat}`}{data.next_flight.gate && ` · Gate ${data.next_flight.gate}`}
                      {data.next_flight.booking_ref && ` · PNR ${data.next_flight.booking_ref}`}</div>
                  </div>
                )}
                {data.hotels?.length > 0 && (
                  <div className="border border-amber-800 bg-amber-950/40 p-4" data-testid="emergency-hotel">
                    <div className="text-xs font-mono uppercase tracking-widest text-amber-400 flex items-center gap-1"><Hotel className="w-3.5 h-3.5" /> Hotel / stay</div>
                    {data.hotels.map((h, i) => (
                      <div key={i} className="mt-1">
                        <div className="font-900">{h.from_place}</div>
                        <div className="text-sm text-zinc-300">{h.date} {h.time?.slice(0, 5)}{h.arrive_date ? ` → ${h.arrive_date}` : ""}{h.ref ? ` · Conf ${h.ref}` : ""}</div>
                      </div>
                    ))}
                  </div>
                )}
                {data.contacts?.length > 0 && (
                  <div className="border border-emerald-800 bg-emerald-950/40 p-4" data-testid="emergency-contacts">
                    <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> Contacts</div>
                    {data.contacts.map((c, i) => (
                      <a key={i} href={`tel:${c.phone}`} className="flex items-center justify-between py-2 border-b border-emerald-900/60 last:border-0 hover:text-emerald-300">
                        <span className="font-900">{c.name}{c.relation ? ` (${c.relation})` : ""}</span>
                        <span className="font-mono text-lg">{c.phone}</span>
                      </a>
                    ))}
                  </div>
                )}
                {data.insurance?.length > 0 && (
                  <div className="border border-violet-800 bg-violet-950/40 p-4" data-testid="emergency-insurance">
                    <div className="text-xs font-mono uppercase tracking-widest text-violet-400 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Travel insurance</div>
                    {data.insurance.map((p, i) => (
                      <div key={i} className="mt-1 text-sm">
                        <span className="font-900">{p.holder_name || "Policy"}</span>{p.number && <span className="font-mono"> · № {p.number}</span>}
                        {p.notes && <div className="text-zinc-300">{p.notes}</div>}
                      </div>
                    ))}
                  </div>
                )}
                {data.pickups?.length > 0 && (
                  <div className="border border-zinc-700 p-4">
                    <div className="text-xs font-mono uppercase tracking-widest text-zinc-400">Pickup / transfer</div>
                    {data.pickups.map((p, i) => (
                      <div key={i} className="text-sm mt-1">{p.date} {p.time?.slice(0, 5)} · {p.from_place}{p.to_place ? ` → ${p.to_place}` : ""}{p.passenger ? ` · ${p.passenger}` : ""}</div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-zinc-500">Shows only what you stored — no passports or sensitive documents. Keep a PDF copy offline too.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
